import React from 'react';
import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { CloseOutlined, MailOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChoiceCard from './ChoiceCard';
import { signupPalette, signupGradient } from './signupTheme';

const { Text } = Typography;

type Props = {
    enabled: boolean | undefined;
    email: string;
    onToggle: (enabled: boolean) => void;
    onEmailChange: (value: string) => void;
    onContinue: () => void;
    /** Email du parent : sert à interdire la saisie de la même adresse. */
    parentEmail: string;
};

/** Étape 4 — création facultative d'un second compte parent. */
const StepSpouse: React.FC<Props> = ({ enabled, email, onToggle, onEmailChange, onContinue, parentEmail }) => {
    const { t } = useTranslation();

    const trimmed = email.trim().toLowerCase();
    const sameAsParent = trimmed !== '' && trimmed === parentEmail.trim().toLowerCase();
    const emailValid = /^\S+@\S+\.\S+$/.test(trimmed);
    const canContinue = enabled === false || (enabled === true && emailValid && !sameAsParent);

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <ChoiceCard
                    icon={<UsergroupAddOutlined />}
                    label={t('signup.spouse.yes', 'Oui, créer un compte conjoint')}
                    description={t('signup.spouse.yesHint', 'Il suivra les mêmes enfants, sans surcoût.')}
                    selected={enabled === true}
                    onSelect={() => onToggle(true)}
                />
                <ChoiceCard
                    icon={<CloseOutlined />}
                    label={t('signup.spouse.no', 'Non, pas pour l’instant')}
                    description={t('signup.spouse.noHint', 'Vous pourrez l’ajouter plus tard.')}
                    selected={enabled === false}
                    onSelect={() => onToggle(false)}
                />
            </div>

            {enabled === true && (
                <Form layout="vertical" requiredMark={false}>
                    <Form.Item
                        label={<Text style={{ fontWeight: 500 }}>{t('signup.spouse.emailLabel', 'Adresse email du conjoint')}</Text>}
                        style={{ marginBottom: 8 }}
                    >
                        <Input
                            autoFocus
                            size="large"
                            type="email"
                            inputMode="email"
                            value={email}
                            onChange={(e) => onEmailChange(e.target.value)}
                            onPressEnter={() => canContinue && onContinue()}
                            prefix={<MailOutlined style={{ color: signupPalette.textMuted }} />}
                            placeholder={t('signup.spouse.emailPlaceholder', 'conjoint@exemple.com')}
                            style={{ borderRadius: 12, padding: '12px 16px', fontSize: 15 }}
                        />
                    </Form.Item>
                    {sameAsParent && (
                        <Alert
                            type="error"
                            showIcon
                            message={t('signup.spouse.sameEmail', 'Cette adresse est déjà la vôtre — utilisez-en une autre.')}
                        />
                    )}
                    {!sameAsParent && (
                        <Text style={{ color: signupPalette.textSecondary, fontSize: 13 }}>
                            {t('signup.spouse.hint', 'Il recevra son propre lien pour définir son mot de passe.')}
                        </Text>
                    )}
                </Form>
            )}

            <Button
                type="primary"
                size="large"
                block
                disabled={!canContinue}
                onClick={onContinue}
                style={{ height: 52, borderRadius: 12, border: 'none', background: signupGradient, fontWeight: 600, fontSize: 16 }}
            >
                {t('signup.actions.continue', 'Continuer')}
            </Button>
        </Space>
    );
};

export default StepSpouse;
